
import React from 'react';
import { Input } from 'antd';
import type { InputRef } from 'antd';

type PhoneInputProps = {
  value?: string;
  onChange?: (v: string) => void;
  maxLength?: number;
  placeholder?: string;
};

const formatUSPhone = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 10);

  if (digits.length <= 3) {
    return digits;
  }

  if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export function PhoneNumberInput({
  value = '',
  onChange,
  maxLength = 10,
  placeholder = '(XXX) XXX-XXXX',
}: PhoneInputProps) {
  const ref = React.useRef<InputRef>(null);

  const digits = (value ?? '').replace(/\D/g, '').slice(0, maxLength);
  const formattedValue = formatUSPhone(digits);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = e.target.value.replace(/\D/g, '').slice(0, maxLength);
    onChange?.(nextValue);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    const el = ref.current?.input;
    if (!el) return;

    const pastedDigits = e.clipboardData
      .getData('text')
      .replace(/\D/g, '');

    const start = el.selectionStart ?? formattedValue.length;
    const end = el.selectionEnd ?? formattedValue.length;

    // Count digits before the selection
    const digitStart = formattedValue
      .slice(0, start)
      .replace(/\D/g, '').length;

    const digitEnd = formattedValue
      .slice(0, end)
      .replace(/\D/g, '').length;

    const nextValue = (
      digits.slice(0, digitStart) +
      pastedDigits +
      digits.slice(digitEnd)
    ).slice(0, maxLength);

    onChange?.(nextValue);
  };

  return (
    <Input
      ref={ref}
      size='large'
      style={{
        height: '48px',
        fontSize: '16px',
        fontWeight: '500',
        borderRadius: '4px',
      }}
      value={formattedValue}
      onChange={handleChange}
      onPaste={handlePaste}
      placeholder={placeholder}
      maxLength={14}
      inputMode='numeric'
      autoComplete='tel-national'
    />
  );
}

export default PhoneNumberInput;
